import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from './firebase';
import { collection, query, onSnapshot, doc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ChevronLeft, ChevronRight, X, Trash2, CalendarClock } from 'lucide-react';
import { deleteToRecycleBin } from './recycleBinUtil';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = toIso(new Date());

const HRCalendar = () => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [rescheduling, setRescheduling] = useState(null); // task being rescheduled
  const [rescheduleDate, setRescheduleDate] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'hrTasks')), (snap) => {
      setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const grid = useMemo(() => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  }, [cursor]);

  const tasksByDate = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!map[t.date]) map[t.date] = [];
      map[t.date].push(t);
    });
    return map;
  }, [tasks]);

  const changeMonth = (delta) => {
    setCursor((c) => { const d = new Date(c); d.setMonth(d.getMonth() + delta); return d; });
  };

  const addTask = async () => {
    if (!newNote.trim() || !selectedDate) return;
    await addDoc(collection(db, 'hrTasks'), {
      date: selectedDate, note: newNote.trim(), status: 'Pending',
      createdAt: serverTimestamp(), createdBy: auth.currentUser?.email || ''
    });
    setNewNote('');
  };

  const toggleStatus = async (task) => {
    await updateDoc(doc(db, 'hrTasks', task.id), {
      status: task.status === 'Complete' ? 'Pending' : 'Complete',
      completedAt: task.status === 'Complete' ? null : serverTimestamp()
    });
  };

  const deleteTask = async (task) => {
    if (!window.confirm('Delete this task? You can restore it from the Recycle Bin.')) return;
    await deleteToRecycleBin({ collectionName: 'hrTasks', id: task.id, data: task, label: 'HR Task' });
  };

  const openReschedule = (task) => {
    setRescheduling(task);
    setRescheduleDate(task.date);
  };

  const confirmReschedule = async () => {
    if (!rescheduling || !rescheduleDate) return;
    await updateDoc(doc(db, 'hrTasks', rescheduling.id), { date: rescheduleDate });
    setRescheduling(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CalendarClock size={18} color="#4a00e0" />
          <h3 style={{ margin: 0 }}>{MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}</h3>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => changeMonth(-1)} style={{ padding: '6px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}><ChevronLeft size={16} /></button>
          <button onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Today</button>
          <button onClick={() => changeMonth(1)} style={{ padding: '6px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}><ChevronRight size={16} /></button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px', marginBottom: '6px' }}>
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: '#888', padding: '4px' }}>{w}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
        {grid.map((date, i) => {
          if (!date) return <div key={i} />;
          const iso = toIso(date);
          const dayTasks = tasksByDate[iso] || [];
          const hasPending = dayTasks.some((t) => t.status !== 'Complete');
          const hasComplete = dayTasks.some((t) => t.status === 'Complete');
          const isToday = iso === todayIso;
          return (
            <div
              key={i}
              onClick={() => setSelectedDate(iso)}
              style={{
                minHeight: '70px', padding: '8px', borderRadius: '10px', cursor: 'pointer',
                border: isToday ? '2px solid #4a00e0' : '1px solid #f0f0f0',
                background: dayTasks.length > 0 ? '#f8f9ff' : '#fff'
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: isToday ? 'bold' : 'normal', color: isToday ? '#4a00e0' : '#333' }}>{date.getDate()}</div>
              <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                {hasPending && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />}
                {hasComplete && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />}
              </div>
              {dayTasks.length > 0 && <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>{dayTasks.length} task{dayTasks.length > 1 ? 's' : ''}</div>}
            </div>
          );
        })}
      </div>

      {selectedDate && (
        <div onClick={() => setSelectedDate(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '420px', maxWidth: '95%', maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setSelectedDate(null)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>Tasks — {selectedDate}</h3>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
              <input className="input-field" placeholder="Add a note for this date..." value={newNote} onChange={(e) => setNewNote(e.target.value)} style={{ flex: 1, marginBottom: 0, width: 'auto' }} onKeyDown={(e) => e.key === 'Enter' && addTask()} />
              <button onClick={addTask} style={{ padding: '0 20px', borderRadius: '12px', border: 'none', background: 'linear-gradient(45deg, #4a00e0, #8e2de2)', color: '#fff', fontWeight: 'bold', cursor: 'pointer', flexShrink: 0 }}>Add</button>
            </div>

            {(tasksByDate[selectedDate] || []).length === 0 && (
              <p style={{ color: '#999', fontSize: '13px', textAlign: 'center' }}>No tasks for this date yet.</p>
            )}
            {(tasksByDate[selectedDate] || []).map((t) => (
              <div key={t.id} style={{ border: '1px solid #f0f0f0', borderRadius: '12px', padding: '12px', marginBottom: '10px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', textDecoration: t.status === 'Complete' ? 'line-through' : 'none', color: t.status === 'Complete' ? '#999' : '#333' }}>{t.note}</p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ padding: '3px 9px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', background: t.status === 'Complete' ? '#eafff3' : '#fff3cd', color: t.status === 'Complete' ? '#218c61' : '#8a6d3b' }}>{t.status}</span>
                  <button onClick={() => toggleStatus(t)} style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '6px', border: 'none', background: t.status === 'Complete' ? '#f59e0b' : '#10b981', color: '#fff', cursor: 'pointer' }}>
                    {t.status === 'Complete' ? 'Mark Pending' : 'Mark Complete'}
                  </button>
                  {t.status !== 'Complete' && (
                    <button onClick={() => openReschedule(t)} style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Reschedule</button>
                  )}
                  <button onClick={() => deleteTask(t)} style={{ padding: '4px 8px', borderRadius: '6px', border: 'none', background: '#ffeded', color: 'red', cursor: 'pointer', marginLeft: 'auto' }}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rescheduling && (
        <div onClick={() => setRescheduling(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '25px', borderRadius: '20px', width: '320px', maxWidth: '95%' }}>
            <h3 style={{ marginTop: 0 }}>Reschedule Task</h3>
            <input className="input-field" type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={() => setRescheduling(null)} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmReschedule} className="btn-confirm" style={{ flex: 1, padding: '10px' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRCalendar;
